export interface AudioAnalysisData {
  bars: Array<{ start: number; duration: number; confidence: number }>;
  beats: Array<{ start: number; duration: number; confidence: number }>;
  sections: Array<{
    start: number;
    duration: number;
    loudness: number;
    tempo: number;
    key: number;
    mode: number;
  }>;
  segments: Array<{
    start: number;
    duration: number;
    loudness_start: number;
    loudness_max: number;
    pitches: number[];
    timbre: number[];
  }>;
  track: {
    tempo: number;
    key: number;
    mode: number;
    time_signature: number;
    duration: number;
    loudness: number;
    energy: number;
    danceability: number;
    valence: number;
  };
}

export class AudioAnalysisVisualizer {
  private analysisData: AudioAnalysisData | null = null;
  private currentPosition: number = 0;
  private isPlaying: boolean = false;
  private animationFrame: number | null = null;
  private startTime: number = 0;
  private pauseTime: number = 0;
  private onUpdate: ((data: {
    beat: number;
    bar: number;
    energy: number;
    loudness: number;
    pitches: number[];
  }) => void) | null = null;

  setAnalysisData(data: AudioAnalysisData): void {
    this.analysisData = data;
  }

  setUpdateCallback(callback: (data: any) => void): void {
    this.onUpdate = callback;
  }

  start(position: number = 0): void {
    if (!this.analysisData) return;

    this.isPlaying = true;
    this.startTime = Date.now() - position * 1000;
    this.animate();
  }

  pause(): void {
    this.isPlaying = false;
    this.pauseTime = this.currentPosition;
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }
  }

  resume(): void {
    if (!this.analysisData) return;

    this.isPlaying = true;
    this.startTime = Date.now() - this.pauseTime * 1000;
    this.animate();
  }

  seek(position: number): void {
    this.currentPosition = position;
    this.startTime = Date.now() - position * 1000;
    if (this.isPlaying) {
      this.animate();
    }
  }

  private animate = (): void => {
    if (!this.isPlaying || !this.analysisData) return;

    // Calculate current position in seconds with 100ms offset (animation leads audio)
    this.currentPosition = (Date.now() - this.startTime) / 1000 + 0.1;

    // Find current beat
    const currentBeat = this.findCurrentItem(this.analysisData.beats, this.currentPosition);
    const currentBar = this.findCurrentItem(this.analysisData.bars, this.currentPosition);
    const currentSegment = this.findCurrentItem(this.analysisData.segments, this.currentPosition);

    // Calculate beat intensity (0-1 based on position within beat)
    let beatIntensity = 0;
    if (currentBeat) {
      const beatProgress = (this.currentPosition - currentBeat.start) / currentBeat.duration;
      // Create a pulse effect that peaks at the start of each beat
      beatIntensity = Math.max(0, 1 - beatProgress) * currentBeat.confidence;
    }

    // Calculate bar intensity
    let barIntensity = 0;
    if (currentBar) {
      const barProgress = (this.currentPosition - currentBar.start) / currentBar.duration;
      barIntensity = Math.max(0, 1 - barProgress * 0.5) * currentBar.confidence;
    }

    // Get current segment data for more detail
    let energy = this.analysisData.track.energy;
    let loudness = this.analysisData.track.loudness;
    let pitches: number[] = [];

    if (currentSegment) {
      // Normalize loudness to 0-1 range (Spotify loudness is in dB, typically -60 to 0)
      loudness = Math.max(0, (currentSegment.loudness_max + 60) / 60);
      pitches = currentSegment.pitches;
      
      // Interpolate energy based on segment
      energy = this.analysisData.track.energy * (1 + (currentSegment.loudness_max + 60) / 120);
    } else {
      // No segment data - normalize track loudness (dB range -60 to 0)
      loudness = Math.max(0, Math.min(1, (loudness + 60) / 60));
    }

    // Send update to callback
    if (this.onUpdate) {
      this.onUpdate({
        beat: beatIntensity,
        bar: barIntensity,
        energy: energy,
        loudness: loudness,
        pitches: pitches,
      });
    }

    // Continue animation
    this.animationFrame = requestAnimationFrame(this.animate);
  };

  private findCurrentItem<T extends { start: number; duration: number }>(
    items: T[],
    position: number
  ): T | null {
    // Binary search for efficiency
    let left = 0;
    let right = items.length - 1;

    while (left <= right) {
      const mid = Math.floor((left + right) / 2);
      const item = items[mid];

      if (position >= item.start && position < item.start + item.duration) {
        return item;
      } else if (position < item.start) {
        right = mid - 1;
      } else {
        left = mid + 1;
      }
    }

    return null;
  }

  stop(): void {
    this.isPlaying = false;
    this.currentPosition = 0;
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }
  }
}
